<script setup lang="ts">
import { useAdminGetAiJob } from '@swipe4work/api-orval'
import { AiJobStatus, type AiJobRating } from '@swipe4work/models-orval'
import type { MenuItem } from 'primevue/menuitem'
import ProfileHeader, { type ProfileHeaderButton, type ProfileHeaderItem } from '@/shared/components/layouts/ProfileHeader.vue'
import JobAgentDataCard from '@/features/tools/components/job-agent/JobAgentDataCard.vue'
import JobAgentSectionsCard from '@/features/tools/components/job-agent/JobAgentSectionsCard.vue'
import AiJobReviewCard from '@/features/ai-jobs/components/AiJobReviewCard.vue'
import { usePermissions } from '@/shared/composables/UsePermissions'
import { useTopBarTitle } from '@/shared/composables/UseTopBarTitle'
import { DateFormat, useFormatDate } from '@/shared/composables/UseFormatDate'
import { useAiJobActions } from '@/features/ai-jobs/composables/useAiJobActions'
import { useAiJobReview } from '@/features/ai-jobs/composables/useAiJobReview'
import {
  aiJobRatingIcons,
  aiJobRatingLabels,
  aiJobRatingSeverities,
  aiJobStatusLabels,
  aiJobStatusSeverities,
  formatAiJobSalary,
} from '@/features/ai-jobs/aiJobMeta'

definePage({
  path: '/ai-jobs/:id',
  name: 'ai-jobs.detail',
  meta: {
    title: 'AI-vacature',
    breadcrumbParent: 'ai-jobs.overview',
    permission: {
      resource: 'Job',
      level: 'Read',
    },
  },
})

/*
 * Read-only view of one saved AiJob: the same data/sections cards as the AI-agents page, without
 * the agent controls. Refining happens on the agent page; converting creates the draft job.
 */
const route = useRoute('ai-jobs.detail')
const router = useRouter()
const { can } = usePermissions()
const { convert, remove, isConverting } = useAiJobActions()
const { openReviewDialog, clearReview, isClearing } = useAiJobReview()

const aiJobId = computed(() => Number((route.params as { id: string }).id))
const { data: aiJob, isPending, isError } = useAdminGetAiJob(aiJobId, { query: { staleTime: 0 } })

useTopBarTitle(() => aiJob.value?.title)

const isDraft = computed(() => aiJob.value?.status === AiJobStatus.Draft)
const canWrite = computed(() => can('Job', 'Write'))
const canConvert = computed(() => isDraft.value && canWrite.value && aiJob.value?.data.jobFunction != null)

// --- Review (thumbs up/down) ---
function openReview(initialRating?: AiJobRating) {
  if (!aiJob.value) return
  void openReviewDialog({
    aiJobId: aiJob.value.id,
    title: aiJob.value.title,
    review: aiJob.value.review ?? null,
    initialRating,
  })
}

const headerItems = computed<ProfileHeaderItem[]>(() => {
  if (!aiJob.value) return []
  const job = aiJob.value

  return [
    {
      key: 'Bedrijf',
      icon: 'fa-regular fa-building',
      value: job.companyName,
      to: {
        name: 'companies.[id]',
        params: { id: job.companyId },
      },
    },
    {
      key: 'Functie',
      icon: 'fa-regular fa-briefcase',
      value: job.data.jobFunction?.name ?? 'Geen match in functielijst',
    },
    {
      key: 'Locatie',
      icon: 'fa-regular fa-location-dot',
      value: job.data.locations.map(l => l.name).join(', ') || '—',
      valueSuffix: job.data.isRemote ? '(remote)' : undefined,
    },
    {
      key: 'Salaris',
      icon: 'fa-regular fa-money-bill-wave',
      value: formatAiJobSalary(job.data.salary),
    },
    {
      key: 'Auteur',
      icon: 'fa-regular fa-user',
      value: job.createdByName,
    },
    {
      key: 'Aangemaakt',
      icon: 'fa-regular fa-calendar',
      value: useFormatDate(job.createdAt, DateFormat.DateTime),
      valueSuffix: job.updatedAt > job.createdAt ? `(bijgewerkt ${useFormatDate(job.updatedAt, DateFormat.DateTime)})` : undefined,
    },
  ]
})

const primaryButtons = computed<ProfileHeaderButton[]>(() => {
  if (!aiJob.value) return []
  const job = aiJob.value

  if (job.jobId != null) {
    return [
      {
        label: 'Bekijk vacature',
        icon: 'fa-regular fa-briefcase',
        onClick: () => router.push({
          name: 'vacatures.detail',
          params: { id: job.jobId! },
        }),
      }, 
    ]
  }

  return [
    {
      label: 'Bewerken in agent',
      icon: 'fa-regular fa-robot',
      severity: 'secondary',
      outlined: true,
      onClick: () => router.push({
        name: '/tools/AiAgents',
        query: { aiJobId: String(job.id) },
      }),
    },
    {
      label: 'Omzetten naar vacature',
      icon: 'fa-regular fa-wand-magic-sparkles',
      onClick: () => {
        if (canConvert.value) {
          convert({
            id: job.id,
            title: job.title,
            isReviewed: job.review != null,
          })
        }
      },
    },
  ]
})

const menuItems = computed<MenuItem[]>(() => [
  {
    label: 'Verwijderen',
    icon: can('Job', 'Delete') ? 'fa-regular fa-trash' : 'fa-regular fa-lock',
    disabled: !can('Job', 'Delete'),
    command: async () => {
      if (!aiJob.value) return
      if (await remove(aiJob.value)) {
        await router.push({ name: 'ai-jobs.overview' })
      }
    },
  },
])

const showVacancyText = ref(false)
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="isPending" class="flex flex-col gap-2">
      <Skeleton height="8rem" />
      <Skeleton v-for="n in 3" :key="n" height="3rem" />
    </div>

    <Message v-else-if="isError || !aiJob" severity="error">
      Deze AI-vacature kon niet worden geladen.
    </Message>

    <template v-else>
      <ProfileHeader
        :avatar-label="aiJob.title.charAt(0).toUpperCase()"
        :title="aiJob.title"
        :subtitle="aiJob.data.functionTitle"
        subtitle-icon="fa-regular fa-robot"
        :items="headerItems"
        :primary-buttons="primaryButtons"
        :menu-items="menuItems"
      >
        <template #banner-actions>
          <Tag
            v-if="aiJob.review"
            :value="aiJobRatingLabels[aiJob.review.rating]"
            :severity="aiJobRatingSeverities[aiJob.review.rating]"
            :icon="aiJobRatingIcons[aiJob.review.rating]"
          />
          <Tag
            :value="aiJobStatusLabels[aiJob.status]"
            :severity="aiJobStatusSeverities[aiJob.status]"
          />
        </template>
      </ProfileHeader>

      <!-- One advisory at a time: why converting is blocked, or where the job went. -->
      <Message v-if="aiJob.jobId != null" severity="success">
        Deze AI-vacature is omgezet naar een vacature en kan niet meer worden gewijzigd.
        <RouterLink :to="{ name: 'vacatures.detail', params: { id: aiJob.jobId } }" class="ml-1 font-medium underline">
          Bekijk de vacature
        </RouterLink>
      </Message>
      <Message v-else-if="!aiJob.data.jobFunction" severity="warn">
        De agent vond geen passende functie in de functielijst. Laat de agent eerst een functie kiezen
        (bewerken in agent) voordat je deze AI-vacature omzet.
      </Message>
      <Message v-else-if="isConverting" severity="info">
        De vacature wordt aangemaakt…
      </Message>

      <AiJobReviewCard
        :review="aiJob.review ?? null"
        :can-write="canWrite"
        :busy="isClearing"
        @rate="openReview"
        @edit="openReview()"
        @clear="clearReview(aiJob.id)"
      />

      <div class="grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2">
        <JobAgentDataCard :data="aiJob.data" :tool-calls="aiJob.toolCalls" readonly />
        <JobAgentSectionsCard :sections="aiJob.sections" readonly />
      </div>

      <Card>
        <template #title>
          <div class="flex items-center gap-2">
            <span>Ingevoerde vacaturetekst</span>
            <Button
              :label="showVacancyText ? 'Verberg' : 'Toon'"
              severity="secondary"
              text
              size="small"
              :icon="showVacancyText ? 'fa-regular fa-chevron-up' : 'fa-regular fa-chevron-down'"
              class="ml-auto"
              @click="showVacancyText = !showVacancyText"
            />
          </div>
        </template>
        <template #content>
          <p v-if="showVacancyText" class="whitespace-pre-wrap text-sm text-surface-700">
            {{ aiJob.vacancyText }}
          </p>
          <p v-else class="truncate text-sm text-surface-500">
            {{ aiJob.vacancyText }}
          </p>
        </template>
      </Card>
    </template>
  </div>
</template>
